/**
 * Order-status label resolver.
 *
 * Returns the i18n key path under which the user-facing status label
 * lives. For INDEPENDENT workshops, routes through
 * `status.independent.*` so e.g. `READY_FOR_PICKUP` reads as
 * "Getting delivered" instead of "Ready for Pickup". Everyone else
 * (admins, agents, PRIVATE workshops, suppliers) gets the original
 * `status.*` namespace.
 */
import { OrderStatus, User } from '../types';
import { isIndependentWorkshop } from './workshopType';

const STATUS_KEY_MAP: Record<OrderStatus, string> = {
  [OrderStatus.PENDING]: 'pending',
  [OrderStatus.QUOTED]: 'quoted',
  [OrderStatus.PR]: 'pr',
  [OrderStatus.READY_FOR_PICKUP]: 'readyForPickup',
  [OrderStatus.INVOICE_REQUEST]: 'invoiceRequest',
  [OrderStatus.INVOICED]: 'invoiced',
  [OrderStatus.DELIVERED]: 'delivered',
  [OrderStatus.CANCELED]: 'cancelled',
};

/**
 * Resolve the short slug for an OrderStatus (e.g. `readyForPickup`).
 * No namespace prefix.
 */
export function getStatusSlug(status: OrderStatus | string): string {
  const s = (typeof status === 'string' ? status : String(status)) as OrderStatus;
  return STATUS_KEY_MAP[s] || String(status).toLowerCase();
}

/**
 * Resolve the full i18n key for an OrderStatus, accounting for the
 * current user's workshop type.
 *   • INDEPENDENT workshop → `status.independent.<slug>`
 *   • everyone else        → `status.<slug>`
 */
export function getStatusKey(
  status: OrderStatus | string,
  user: User | null | undefined,
): string {
  const slug = getStatusSlug(status);
  if (isIndependentWorkshop(user)) {
    return `status.independent.${slug}`;
  }
  return `status.${slug}`;
}
